import { LeftCircleFilled, RightCircleFilled } from '@ant-design/icons';
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { Button, DatePicker, message, Switch } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import JSZip from 'jszip';
import { FC, ReactElement, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { useKeys } from '../keys';
import { useLocalStorage } from '../localstorage';
import { get_imu_file, get_imu_infos, get_imu_list } from '../services/api.service';
import { formatdateYYYYMMDD, ImuDataBlock, ImuFileBlock, ImuSample, MLIMU, MLImuMeeting } from '../types';
import './imu.scss';
import { ImuGraph } from '../components/imugraph';

/* -------------------------------------------------------------------------------- *\
|                               Google map
\* -------------------------------------------------------------------------------- */
const API_KEY = 'AIzaSyAYJ0T0QfMpPex9whIoratlZIIiEaJXAq8';

const MAP_CENTER = { lat: 43.628908, lng: 3.909600 };
const MAP_STYLE = { width: "100%", height: "100%" };
const MAP_ZOOM = 16;

const MAP_OPTIONS = {
    streetViewControl: false,
    mapTypeId: 'satellite',
    scaleControl: true,
    fullscreenControl: true,
    fullscreenControlOptions: { position: 6.0},
    gestureHandling: "cooperative",
    disableDoubleClickZoom: true,
    minZoom: 3,
    maxZoom: 20,
    mapTypeControl: false,
    zoomControl: true,
    clickableIcons: false,
    disableDefaultUI: true
};
/* -------------------------------------------------------------------------------- *\
|                               ImuZipFile
\* -------------------------------------------------------------------------------- */
class ImuZipFile
{
    public name: string = "";
    public number: number = 0;
    public valid: boolean = false;
    public warning: boolean = false;
}
/* -------------------------------------------------------------------------------- *\
|                               decodeUbxNavPvt
\* -------------------------------------------------------------------------------- */
function decodeUbxNavPvt(buffer: ArrayBuffer, offset: number)
{
    if (offset + 92 > buffer.byteLength) return null;
    const v = new DataView(buffer, offset, 92);
    return {
        iTOW:    v.getUint32(0,  true),
        year:    v.getUint16(4,  true),
        month:   v.getUint8(6),
        day:     v.getUint8(7),
        hour:    v.getUint8(8),
        min:     v.getUint8(9),
        sec:     v.getUint8(10),
        valid:   v.getUint8(11),
        tAcc:    v.getUint32(12, true),
        nano:    v.getInt32(16,  true),
        fixType: v.getUint8(20),
        flags:   v.getUint8(21),
        flags2:  v.getUint8(22),
        numSV:   v.getUint8(23),
        lon:     v.getInt32(24,  true) / 1e7,
        lat:     v.getInt32(28,  true) / 1e7,
        height:  v.getInt32(32,  true) / 1000,
        hMSL:    v.getInt32(36,  true) / 1000,
        hAcc:    v.getUint32(40, true) / 1000,
        vAcc:    v.getUint32(44, true) / 1000,
        velN:    v.getInt32(48,  true) / 1000,
        velE:    v.getInt32(52,  true) / 1000,
        velD:    v.getInt32(56,  true) / 1000,
        gSpeed:  v.getInt32(60,  true) / 1000,
        headMot: v.getInt32(64,  true) / 1e5,
        sAcc:    v.getUint32(68, true) / 1000,
        headAcc: v.getUint32(72, true) / 1e5,
        pDOP:    v.getUint16(76, true) / 100,
        // bytes 78-83: reserved
        headVeh: v.getInt32(84,  true) / 1e5,
        magDec:  v.getInt16(88,  true) / 100,
        magAcc:  v.getUint16(90, true) / 100,
    };
}
/* -------------------------------------------------------------------------------- *\
|                               decodeImuFile
\* -------------------------------------------------------------------------------- */
function decodeImuFile(buffer: ArrayBuffer): ImuFileBlock[]
{
    const view = new DataView(buffer);
    const blocks: ImuFileBlock[] = [];
    let offset = 0;

    while (offset + 2052 <= buffer.byteLength)
    {
        // Check M1 header (0x4d 0x31)
        const header = view.getUint16(offset, false);
        if (header !== 0x4d31)
        {
            offset += 2052;
            continue;
        }

        const blockNumber = view.getUint16(offset + 2, false);
        const dataOffset = offset + 4;

        // Check I1 header (0x49 0x31) in data block
        const dataHeader = view.getUint16(dataOffset, false);
        let dataBlock: ImuDataBlock | null = null;

        if (dataHeader === 0x4931)
        {
            const timestamp = view.getUint32(dataOffset + 2, false);
            const latitude = view.getInt32(dataOffset + 6, false) / 10000000;
            const longitude = view.getInt32(dataOffset + 10, false) / 10000000;

            const samples: ImuSample[] = [];
            let sampleOffset = dataOffset + 14;

            for (let i = 0; i < 160; i++)
            {
                samples.push({
                    gyroX: view.getInt16(sampleOffset, true),
                    gyroY: view.getInt16(sampleOffset + 2, true),
                    gyroZ: view.getInt16(sampleOffset + 4, true),
                    xlX: view.getInt16(sampleOffset + 6, true),
                    xlY: view.getInt16(sampleOffset + 8, true),
                    xlZ: view.getInt16(sampleOffset + 10, true),
                });
                sampleOffset += 12;
            }

            const ubxNavPvt = decodeUbxNavPvt(buffer, sampleOffset);

            // Validation: timestamp between 2020 and 2100, lat/lon in valid range
            const TIMESTAMP_2020 = 1577836800;
            const TIMESTAMP_2100 = 4102444800;
            const validTimestamp = timestamp >= TIMESTAMP_2020 && timestamp <= TIMESTAMP_2100;
            const validLatitude = latitude >= -90 && latitude <= 90;
            const validLongitude = longitude >= -180 && longitude <= 180;
            const validZero = longitude !== 0 && longitude !== 0;
            const valid = validTimestamp && validLatitude && validLongitude && validZero;

            dataBlock = { timestamp, latitude, longitude, samples, ubxNavPvt, valid };
        }
        else
        {
            console.error(`Invalid data header in block ${blockNumber}: 0x${dataHeader.toString(16)}`);
        }

        blocks.push({ blockNumber, data: dataBlock });
        offset += 2052;
    }

    return blocks;
}
/* -------------------------------------------------------------------------------- *\
|                               ImuPage
\* -------------------------------------------------------------------------------- */
export const ImuPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [lastselecteddate, setlastselecteddate] = useLocalStorage('lastdate_imu', formatdateYYYYMMDD(new Date()));
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentdate, setCurrentDate] = useState<string>(lastselecteddate);
    const [daypicker, setDaypicker] = useState<Dayjs>(dayjs(lastselecteddate));

    const [meetings, setMeetings] = useState<MLImuMeeting[]>([]);
    const [zipFiles, setZipFiles] = useState<ImuZipFile[]>([]);
    const [currentZip, setCurrentZip] = useState<JSZip | null>(null);
    const [currentImu, setCurrentImu] = useState<MLIMU | null>(null);
    const [currentFile, setCurrentFile] = useState<string>('');
    const [imuBlocks, setImuBlocks] = useState<ImuFileBlock[]>([]);
    const [selectedblock, setSelectedBlock] = useState<ImuFileBlock | null>(null);
    const [gapNavIdx, setGapNavIdx] = useState<number>(0);

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [markers, setMarkers] = useState<ReactElement[]>([]);
    const [maplines, setMapLines] = useState<google.maps.Polyline[]>([]);
    const [showNormalPos, setShowNormalPos] = useState<boolean>(true);
    const [showPvtPos, setShowPvtPos] = useState<boolean>(false);

    const [center, setCenter] = useState(MAP_CENTER);
    const zoom = MAP_ZOOM;
    const options = MAP_OPTIONS;

    const [mapwidth] = useState(MAP_STYLE.width);
    const [mapheight] = useState(MAP_STYLE.height);

    /* -------------------------------------------------------------------------------- *\
    |                               cleanmap
    \* -------------------------------------------------------------------------------- */
    function cleanmap()
    {
        if (map)
        {
            for(const pl of maplines)
            {
                pl.setMap(null);
            }
            setMapLines([]);
            setMarkers([]);
        }   
    }
    var g_inrefresh: boolean = false;
    /* -------------------------------------------------------------------------------- *\
    |                               refreshFiles
    \* -------------------------------------------------------------------------------- */
    const refreshFiles = async (currentdate: string) =>
    {
        if (g_inrefresh) return Promise.resolve();

        g_inrefresh = true;

        setLoading(true);
        try
        {
            const files: MLIMU[] = await get_imu_list(currentdate);
            const siclist: string[] = Array.from(new Set(files.map(e => e.sic))).sort((a, b) => a.localeCompare(b));
            setMeetings(siclist.map(sic => ({ meetingdate: currentdate, sic: sic, files: files.filter(e => e.sic === sic) })));           
        }
        catch (error: any)
        {
            message.error(error.message);
            if (error.status === 403 || error.status === 401) navigate('/login');            
        }
        g_inrefresh = false;
        setLoading(false);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               changeCurrentDate
    \* -------------------------------------------------------------------------------- */
    const changeCurrentDate = (date: string) =>
    {
        setMeetings([]);
        setZipFiles([]);
        setCurrentZip(null);
        setCurrentImu(null);
        setCurrentDate(date);
        setImuBlocks([]);
        setCurrentFile('');
        setDaypicker(dayjs(date));
        setlastselecteddate(date);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               datepicker_handler
    \* -------------------------------------------------------------------------------- */
    function datepicker_handler(date: any)
    {
        changeCurrentDate(date.format('YYYYMMDD'));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               loadimu
    \* -------------------------------------------------------------------------------- */
    async function loadimu(imufile: MLIMU)
    {
        setLoading(true);
        setZipFiles([]);
        setCurrentZip(null);

        try
        {
            const blob = await get_imu_file(imufile);
            if (!blob)
            {
                message.error('File not found');
                setLoading(false);
                return;
            }

            const zip = await JSZip.loadAsync(blob);
            const files: ImuZipFile[] = [];
            zip.forEach((relativePath) =>
            {
                files.push({name: relativePath, valid: true, warning: false, number: Number(relativePath.split('.')[0])});
            });
            for (const f of files)
            {
                const content = await zip.file(f.name)?.async('arraybuffer');
                if (content)
                {
                    const blocks = decodeImuFile(content);
                    f.valid = blocks.length > 20;
                    f.warning = blocks.length > 20 && (blocks.filter(n => n.data?.valid).length / blocks.length) < 0.5;
                }
            }

            setCurrentImu(imufile);
            setCurrentZip(zip);
            setZipFiles(files.sort((a: ImuZipFile, b: ImuZipFile) => a.number > b.number ? 1 : -1));
            setImuBlocks([]);
            setCurrentFile('');
        }
        catch (error: any)
        {
            message.error(error.message || 'Error loading file');
        }

        setLoading(false);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               loadfile
    \* -------------------------------------------------------------------------------- */
    async function loadfile(file: string)
    {
        if (!currentZip) return;

        setLoading(true);
        setImuBlocks([]);
        setCurrentFile('');
        setSelectedBlock(null);
        cleanmap();

        try
        {
            const content = await currentZip.file(file)?.async('arraybuffer');
            if (!content)
            {
                message.error('File not found in zip');
                setLoading(false);
                return;
            }

            const blocks = decodeImuFile(content);
            setImuBlocks(blocks);
            setCurrentFile(file);
        }
        catch (error: any)
        {
            message.error(error.message || 'Error extracting file');
        }

        setLoading(false);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               scrollToItem
    \* -------------------------------------------------------------------------------- */
    const scrollToItem = (id: string) =>
    {
        const element = document.getElementById(id);
        if (element)
        {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    };
    /* -------------------------------------------------------------------------------- *\
    |                               onKeyDown
    \* -------------------------------------------------------------------------------- */
    const onKeyDown = (event) =>
    {
        if (selectedblock)
        {
            var i = imuBlocks.indexOf(selectedblock);
            const step = event.shiftKey ? 10 : 1;
            if (event.key === 'ArrowUp')
            {
                const newIndex = Math.max(0, i - step);
                if (newIndex !== i)
                {
                    scrollToItem('block_' + newIndex);
                    doselectblock(imuBlocks[newIndex]);
                }
            }
            else if (event.key === 'ArrowDown')
            {
                const newIndex = Math.min(imuBlocks.length - 1, i + step);
                if (newIndex !== i)
                {
                    scrollToItem('block_' + newIndex);
                    doselectblock(imuBlocks[newIndex]);
                }
            }
            event.preventDefault();
        }
    }
    const onKeyUp = () => {};

    useKeys(onKeyDown, onKeyUp, ['ArrowDown', 'ArrowUp']);
    /* -------------------------------------------------------------------------------- *\
    |                               doselectblock
    \* -------------------------------------------------------------------------------- */
    function doselectblock(block: ImuFileBlock)
    {
        setSelectedBlock(block);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               getinfos
    \* -------------------------------------------------------------------------------- */
    async function getinfos(_file: string)
    {
        if (!currentImu) return;
        const data = await get_imu_infos(currentImu);
        if (!data) return;
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${currentImu.date}${currentImu.sic}${currentImu.number}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               getfilebin
    \* -------------------------------------------------------------------------------- */
    async function getfilebin(file: string)
    {
        if (!currentZip) return;
        const content = await currentZip.file(file)?.async('blob');
        if (!content) return;
        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = file;
        a.click();
        URL.revokeObjectURL(url);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() =>
    {
        refreshFiles(currentdate);
        // eslint-disable-next-line
    }, [currentdate]);

    useEffect(() =>
    {
        cleanmap();
        // eslint-disable-next-line
    }, [currentFile, map])

    useEffect(() =>
    {
        /* -------------------------------------------------------------------------------- *\
        |                               addpolyline
        \* -------------------------------------------------------------------------------- */
        function addpolyline(path: google.maps.LatLngLiteral[], color: string) : google.maps.Polyline
        {
            const polyline = new google.maps.Polyline({
                path: path,
                geodesic: true,
                strokeColor: color,
                strokeOpacity: 1.0,
                strokeWeight: 1
            });
            polyline.setMap(map);
            return polyline;
        }
        if (!map) return;

        cleanmap();

        var lines: google.maps.Polyline[] = [];

        var index: number = 0;
        let lastvalid: boolean = false;
        let lastpos: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
        for(const block of imuBlocks)
        {
            if (!block.data || !block.data.valid)
            {
                lastvalid = false;
                continue;
            }

            const pos = { lat: block.data.latitude, lng: block.data.longitude };
            if (lastvalid)
            {
                const yellow = (255 - ((255 * index / imuBlocks.length) | 0)).toString(16).padStart(2, '0');
                const blue = (((255 * index / imuBlocks.length) | 0)).toString(16).padStart(2, '0');
                lines.push(addpolyline([ lastpos, pos ], '#00' + yellow + blue));
            }
            lastpos = pos;
            lastvalid = true;
            index++;
        }
        setMapLines(lines);

        const pts = imuBlocks.filter((p) => p.data && p.data.valid);
        if (imuBlocks.length > 0)
        {
            const center_lat = pts.reduce((sum, p) => sum + (p.data?.latitude ?? 0), 0) / pts.length;
            const center_lon = pts.reduce((sum, p) => sum + (p.data?.longitude ?? 0), 0) / pts.length;

            setCenter({ lat: center_lat, lng: center_lon });
        }
        // eslint-disable-next-line
    }, [imuBlocks, map])

    useEffect(() =>
    {
        if (!map) return;

        var marks: ReactElement[] = [];

        const icon_size = 14;
        const gsize = new window.google.maps.Size(icon_size, icon_size);
        const ganchor = new google.maps.Point(icon_size / 2, icon_size / 2);

        if (showNormalPos && selectedblock && selectedblock.data && selectedblock.data.valid)
        {
            const pos = { lat: selectedblock.data.latitude, lng: selectedblock.data.longitude };
            marks.push(
                <Marker key="marker" position={pos} icon={{ url: `/images/green_dot.png`, scaledSize: gsize, anchor: ganchor}}>
                </Marker>
            );
        }

        const pvt = selectedblock?.data?.ubxNavPvt;
        if (showPvtPos && pvt && pvt.lat >= -90 && pvt.lat <= 90 && pvt.lon >= -180 && pvt.lon <= 180 && pvt.lat !== 0 && pvt.lon !== 0)
        {
            const pos = { lat: pvt.lat, lng: pvt.lon };
            marks.push(
                <Marker key="marker_pvt" position={pos} icon={{ url: `/images/red_dot.png`, scaledSize: gsize, anchor: ganchor}}>
                </Marker>
            );
        }

        setMarkers(marks);
    }, [selectedblock, map, showNormalPos, showPvtPos])

    const gapIndices: number[] = (() => {
        const indices: number[] = [];
        let prev: number | null = null;
        for (let i = 0; i < imuBlocks.length; i++) {
            const itow = imuBlocks[i].data?.ubxNavPvt?.iTOW;
            if (itow !== undefined) {
                if (prev !== null && Math.abs(itow - prev) > 10000) indices.push(i);
                prev = itow;
            }
        }
        return indices;
    })();

    const navigateGap = (dir: number) => {
        const next = Math.max(0, Math.min(gapIndices.length - 1, gapNavIdx + dir));
        setGapNavIdx(next);
        const blockIndex = gapIndices[next];
        if (blockIndex !== undefined) {
            scrollToItem('block_' + blockIndex);
            doselectblock(imuBlocks[blockIndex]);
        }
    };

    return (
        <LayoutSite>
            <div className="imu">
                {loading ? <Loader /> : <></>}
                <div className='imu__wrapper'>
                    <div className="imu__header">
                        <div></div>
                    </div>
                    <div className='imu__calendar'>
                        <div className='imu__calendar__selector'>
                            <div className='imu__label'>Choix du jour :</div>
                            <LeftCircleFilled className='imu__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).subtract(1, 'day').format('YYYYMMDD'))} />
                            <DatePicker defaultValue={dayjs(currentdate)} value={daypicker} onChange={datepicker_handler} format={'DD/MM/YYYY'} />
                            <RightCircleFilled className='imu__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).add(1, 'day').format('YYYYMMDD'))}/>
                        </div>
                        <Button type="primary" onClick={() => changeCurrentDate(formatdateYYYYMMDD(new Date()))}>Aujourd'hui</Button>
                    </div>
                </div>
                <div className="imu__body">
                    <div className="imu__body__title">IMU</div>
                    <div className="imu__body__meetings">
                        {
                            meetings.map((m: MLImuMeeting, imeeting: number) =>
                            {
                                return (
                                    <div className={'imu__body__meeting'} key={'meeting_' + imeeting}>
                                        <div className='imu__body__meeting__title'>{m.sic}</div>
                                        <div className='imu__body__meeting__numbers'>
                                        {
                                            m.files.sort((a: MLIMU, b: MLIMU) => a.number > b.number ? 1 : -1).map((imufile: MLIMU, ifile: number) =>
                                            {
                                                return (
                                                    <div key={'date_' + ifile} className={'imu__body__meeting__file'}>
                                                        <div onClick={() => loadimu(imufile)}>{imufile.number}</div>
                                                    </div>
                                                )
                                            })
                                        }
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                    <LoadScript googleMapsApiKey={API_KEY} >
                    {
                        currentImu &&
                        <>
                            <div className="imu__body__zipfiles">
                                <div className='imu__body__zipfiles__title'>
                                    <div>{currentImu?.date + currentImu?.sic + currentImu?.number}</div>
                                    <Button className='imu__body__zipfiles__buttonmin' size="small" onClick={() => getinfos(currentFile)}>Get infos</Button>
                                </div>
                                <div className={'imu__body__zipfiles__files'}>
                                {
                                    zipFiles.map((z: ImuZipFile, iz: number) =>
                                    {
                                        const activeClass = z.name === currentFile ? ' imu__body__zipfiles__active' : '';
                                        const invalidClass = !z.valid ? ' imu__body__zipfiles__invalid' : (z.warning ? ' imu__body__zipfiles__warning' : '');
                                        return (
                                            <div className={'imu__body__zipfiles__file' + activeClass + invalidClass} key={'zipfile_' + iz}>
                                                <div onClick={() => loadfile(z.name)}>{z.name.split('.')[0]}</div>
                                            </div>
                                        )
                                    })
                                }
                                </div>
                            </div>
                            <div className="imu__body__bottom">
                                <div className='imu__body__infos'>
                                    {
                                        currentFile &&
                                        <>
                                            <div className='imu__body__infos__line'>
                                                <div className='imu__body__infos__line__label'>file:</div>
                                                <div className='imu__body__infos__line__value'>{currentFile}</div>
                                            </div>
                                            <div className='imu__body__infos__line'>
                                                <div className='imu__body__infos__line__label'>blocks:</div>
                                                <div className='imu__body__infos__line__value'>{imuBlocks.length}</div>
                                            </div>
                                            <div className='imu__body__infos__line'>
                                                <div className='imu__body__infos__line__label'>valid:</div>
                                                <div className='imu__body__infos__line__value'>{imuBlocks.filter(b => b.data?.valid).length}</div>
                                            </div>
                                            <div className='imu__body__infos__line'>
                                                <div className='imu__body__infos__line__label'>iTOW gaps:</div>
                                                <div className='imu__body__infos__line__value'>{gapIndices.length}</div>
                                                {gapIndices.length > 0 && <>
                                                    <LeftCircleFilled className='imu__body__infos__arrow' onClick={() => navigateGap(-1)} />
                                                    <RightCircleFilled className='imu__body__infos__arrow' onClick={() => navigateGap(1)} />
                                                </>}
                                            </div>
                                            
                                            <div className='imu__body__infos__button'>
                                                <Button size="small" onClick={() => getfilebin(currentFile)}>Get bin</Button>
                                            </div>
                                            {
                                                selectedblock?.data?.ubxNavPvt &&
                                                (() => {
                                                    const pvt = selectedblock.data!.ubxNavPvt!;
                                                    const FIX_TYPES = ['No fix', 'Dead reck.', '2D', '3D', 'GNSS+DR', 'Time only'];
                                                    const date = `${pvt.year}-${String(pvt.month).padStart(2,'0')}-${String(pvt.day).padStart(2,'0')}`;
                                                    const time = `${String(pvt.hour).padStart(2,'0')}:${String(pvt.min).padStart(2,'0')}:${String(pvt.sec).padStart(2,'0')}`;
                                                    return <>
                                                        <div className='imu__body__infos__separator' />
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>iTOW:</div>
                                                            <div className='imu__body__infos__line__value'>{pvt.iTOW}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>iTOW dec:</div>
                                                            <div className='imu__body__infos__line__value'>{(() => { const ms = pvt.iTOW % 1000; const s = Math.floor(pvt.iTOW / 1000) % 60; const m = Math.floor(pvt.iTOW / 60000) % 60; const h = Math.floor(pvt.iTOW / 3600000) % 24; const d = Math.floor(pvt.iTOW / 86400000); return `${d}d ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}.${String(ms).padStart(3,'0')}`; })()}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>date:</div>
                                                            <div className='imu__body__infos__line__value'>{date}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>time:</div>
                                                            <div className='imu__body__infos__line__value'>{time}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>fix:</div>
                                                            <div className='imu__body__infos__line__value'>{pvt.fixType + (pvt.fixType ? (' (' + FIX_TYPES[pvt.fixType] + ')') : '')}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>flags:</div>
                                                            <div className='imu__body__infos__line__value'>0x{pvt.flags.toString(16).padStart(2,'0')}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>flags2:</div>
                                                            <div className='imu__body__infos__line__value'>0x{pvt.flags2.toString(16).padStart(2,'0')}</div>
                                                        </div>
                                                        <div className='imu__body__infos__line'>
                                                            <div className='imu__body__infos__line__label'>numSV:</div>
                                                            <div className='imu__body__infos__line__value'>{pvt.numSV}</div>
                                                        </div>
                                                        <div className='imu__body__infos__separator' />
                                                        {(() => {
                                                            const FLAGS: [number, number, string][] = [
                                                                [pvt.flags,  0, 'gnssFixOK'],
                                                                [pvt.flags,  1, 'diffSoln'],
                                                                [pvt.flags,  5, 'headVehValid'],
                                                                [pvt.flags,  6, 'carrSoln=float'],
                                                                [pvt.flags,  7, 'carrSoln=fixed'],
                                                                [pvt.flags2, 5, 'confirmedAvai'],
                                                                [pvt.flags2, 6, 'confirmedDate'],
                                                                [pvt.flags2, 7, 'confirmedTime'],
                                                            ];
                                                            const active = FLAGS.filter(([byte, bit]) => (byte >> bit) & 1);
                                                            if (active.length === 0) return null;
                                                            return active.map(([,,name]) => (
                                                                <div key={name} className='imu__body__infos__flag'>{name}</div>
                                                            ));
                                                        })()}
                                                    </>;
                                                })()
                                            }
                                        </>
                                    }
                                </div>
                                <div className='imu__body__blocks'>
                                {
                                    (() => {
                                        const gapSet = new Set<number>();
                                        let prev: number | null = null;
                                        for (let i = 0; i < imuBlocks.length; i++) {
                                            const itow = imuBlocks[i].data?.ubxNavPvt?.iTOW;
                                            if (itow !== undefined) {
                                                if (prev !== null && Math.abs(itow - prev) > 10000) gapSet.add(i);
                                                prev = itow;
                                            }
                                        }
                                        return imuBlocks.map((block, i) =>
                                        {
                                            let c = block.blockNumber === selectedblock?.blockNumber ? ' imu__body__blocks__line__active' : '';
                                            const invalidClass = !block.data?.valid ? ' imu__body__blocks__line__invalid' : '';
                                            const gapClass = gapSet.has(i) ? ' imu__body__blocks__line__gap' : '';
                                            return (
                                                <div key={'block_' + i} id={'block_' + i}>
                                                    <div className={'imu__body__blocks__line' + c + invalidClass + gapClass} onClick={() => { doselectblock(block); }}>
                                                        <div>{i + ":"}</div><div>{block.blockNumber}</div>
                                                    </div>
                                                </div>
                                            )
                                        });
                                    })()
                                }
                                </div>
                                <div className='imu__body__visual'>
                                    <ImuGraph
                                        block={selectedblock?.data ?? null}
                                        prevBlock={selectedblock ? (imuBlocks[imuBlocks.indexOf(selectedblock) - 1]?.data ?? null) : null}
                                        nextBlock={selectedblock ? (imuBlocks[imuBlocks.indexOf(selectedblock) + 1]?.data ?? null) : null}
                                    />
                                    <div className='imu__body__map'>
                                        <div className='imu__body__map__controls'>
                                            <label><Switch size="small" checked={showNormalPos} onChange={setShowNormalPos} /> Position</label>
                                            <label><Switch size="small" checked={showPvtPos} onChange={setShowPvtPos} /> PVT</label>
                                        </div>
                                        <GoogleMap
                                            id='themap'
                                            onLoad={(map) => setMap(map)}
                                            mapContainerStyle={{ width: mapwidth, height: mapheight }}
                                            zoom={zoom}
                                            center={center}
                                            options={options}>
                                            { markers }
                                        </GoogleMap>
                                    </div>
                                </div>
                        </div>
                        </>
                    }
                    </LoadScript>
                </div>
            </div>
        </LayoutSite>
    );
}
